import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Add as AddIcon,
    Refresh as RefreshIcon,
    Sort as SortIcon,
    MoreVert as MoreVertIcon,
    ImportExport as ImportExportIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import ClientDialog from '../ClientDialog';
import ImportClientsDialog from '../../ImportClientsDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompanyClientUrlSegment } from '../../../shared/clients/companyClientRouting';
import { setClientArchived } from '../../../shared/clients/clientArchive';
import { listClients, deleteClient } from '../../../api/companyRecords';
import { toJSDate } from '../../../utils/timestampUtils';
import MobileClientCards from './MobileClientCards';
import MobileClientSearchHeader from './MobileClientSearchHeader';
import InviteClientDialog from '../invitations/InviteClientDialog';
import MobileManagementPagination from '../../management-list/mobile/MobileManagementPagination';
import { usePermissions } from '../../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../../company-management/constants/permissions';
import './styles/MobileClients.css';

const MobileClientManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, companyData } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { setNotification } = useNotification();
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isInviteClientOpen, setIsInviteClientOpen] = useState(false);
  
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [listMode, setListMode] = useState(() =>
    location.state?.listMode === 'archived' ? 'archived' : 'active'
  );
  
  const itemsPerPage = 8;

  const handleViewProfile = (client) => {
    navigate(`/app/overview/clients/profile/${getCompanyClientUrlSegment(client)}`);
  };

  const handleNewNotification = useCallback((message, severity) => {
    setNotification({ open: true, message, severity });
  }, [setNotification]);

  const fetchClients = useCallback(async () => {
    if (!user || !companyData) return;

    setIsLoading(true);
    setError(null);
    try {
      const fetchedClients = await listClients({
        companyId: companyData.id,
        archived: listMode === 'archived',
      });
      const clientList = fetchedClients
        .filter((entry) => entry.id !== 'default' && !entry.isSystem && !entry.isDeleted)
        .map((data) => ({
          id: data.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          archived: data.archived === true,
          archivedAt: data.archivedAt || null,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
          name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          searchName: data.searchName || `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase().trim(),
          slug: data.slug || '',
          companyId: companyData.id,
        }));

      setClients(clientList);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(`Failed to fetch data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [user, companyData, listMode]);
  
  const fetchClientsWithAnimation = useCallback(async () => {
    setIsLoading(true);
    await fetchClients();
    setTimeout(() => setIsLoading(false), 800);
  }, [fetchClients]);

  useEffect(() => {
    if (user && companyData) {
      fetchClients();
    }
  }, [user, companyData, fetchClients]);

  const handleAddClient = useCallback(() => {
    setSelectedClient(null);
    setIsClientDialogOpen(true);
  }, []);

  const handleEditClient = useCallback((client) => {
    setSelectedClient(client);
    setIsClientDialogOpen(true);
  }, []);

  const handleSaveClient = useCallback(() => {
    setIsClientDialogOpen(false);
    fetchClients();
  }, [fetchClients]);

  const handleDeleteClient = useCallback(async (client) => {
    if (!user || !companyData) return;

    try {
      await deleteClient({ companyId: companyData.id, id: client.id });
      handleNewNotification('Client deleted successfully', 'success');
      fetchClients();
    } catch (error) {
      handleNewNotification("Failed to delete client. Please try again.", "error");
    }
  }, [fetchClients, handleNewNotification, user, companyData]);

  const handleRestoreClient = useCallback(async (client) => {
    if (!user || !companyData || !hasPermission(PERMISSIONS.CLIENTS.EDIT)) return;

    try {
      await setClientArchived(companyData.id, client.id, false);
      handleNewNotification('Client restored successfully', 'success');
      fetchClients();
    } catch (error) {
      handleNewNotification('Failed to restore client. Please try again.', 'error');
    }
  }, [companyData, fetchClients, handleNewNotification, hasPermission, user]);

  const handleListModeChange = useCallback((mode) => {
    setListMode(mode);
    setCurrentPage(1);
    setSearchTerm('');
    setSortBy(mode === 'archived' ? 'archivedAt' : 'name');
    setSortDirection(mode === 'archived' ? 'desc' : 'asc');
  }, []);

  const handleImportClick = () => {
    setIsImportDialogOpen(true);
    setIsMoreMenuOpen(false);
  };

  const handleInviteClientClick = () => {
    setIsInviteClientOpen(true);
    setIsMoreMenuOpen(false);
  };

  const handleExportClients = useCallback(() => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone'];
    
    const csvRows = [
      headers.join(','),
      ...clients.map(client => [
        client.firstName,
        client.lastName,
        client.email,
        client.phone,
      ].join(','))
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    const url = window.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `clients_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsMoreMenuOpen(false);
  }, [clients]);

  const filteredClients = useMemo(() => {
    const filtered = clients.filter(client => {
      return !searchTerm || 
        client.searchName?.includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
          const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
          comparison = aName.localeCompare(bName);
          break;
        case 'createdAt':
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          comparison = aDate - bDate;
          break;
        case 'archivedAt':
          const aArchived = a.archivedAt?.toDate?.() || a.archivedAt || 0;
          const bArchived = b.archivedAt?.toDate?.() || b.archivedAt || 0;
          comparison = new Date(aArchived).getTime() - new Date(bArchived).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [clients, searchTerm, sortBy, sortDirection]);

  const calculateClientStats = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const activeClients = clients.filter(client => {
      return true;
    }).length;

    const newClientsThisMonth = clients.filter(client => {
      const createdAt = toJSDate(client.createdAt);
      return createdAt && createdAt >= startOfMonth;
    }).length;

    return { activeClients, newClientsThisMonth };
  };

  const { activeClients, newClientsThisMonth } = calculateClientStats();

  useEffect(() => {
    setTotalPages(Math.ceil(filteredClients.length / itemsPerPage));
  }, [filteredClients, itemsPerPage]);

  const handleOpenSortDrawer = () => {
    setIsSortDrawerOpen(true);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection('asc');
    }
    setIsSortDrawerOpen(false);
  };

  const handleMoreClick = () => {
    setIsMoreMenuOpen(true);
  };
  
  const handleMoreClose = () => {
    setIsMoreMenuOpen(false);
  };

  const paginatedClients = useMemo(() => {
    return filteredClients.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredClients, currentPage, itemsPerPage]);

  if (!user || !companyData) {
    return <div className="mobile-clients-page">Loading user and company data...</div>;
  }
  
  return (
    <div className="mobile-clients-page">
      <MobileClientSearchHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSortClick={handleOpenSortDrawer}
        totalClients={clients.length}
        listMode={listMode}
        onListModeChange={handleListModeChange}
      />

      {error ? (
        <div className="mobile-clients-error">
          <p className="mobile-clients-error-title">{error}</p>
          <button
            type="button"
            className="mobile-clients-error-button"
            onClick={fetchClients}
          >
            Try Again
          </button>
        </div>
      ) : (
        <MobileClientCards
          clients={paginatedClients}
          onViewProfile={handleViewProfile}
          onEdit={handleEditClient}
          onDelete={handleDeleteClient}
          onRestoreClient={handleRestoreClient}
          onAddClient={listMode === 'active' ? handleAddClient : null}
          listMode={listMode}
          isLoading={isLoading}
          emptyStateMessage={
            searchTerm
              ? 'Try adjusting your search'
              : listMode === 'archived'
                ? 'No archived clients'
                : 'Add your first client to get started'
          }
        />
      )}

      {totalPages > 1 ? (
        <MobileManagementPagination
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalCount={filteredClients.length}
          itemsPerPage={itemsPerPage}
          resultsNoun="clients"
        />
      ) : null}

      <div
        className={
          'mobile-clients-drawer-backdrop' +
          (isSortDrawerOpen ? ' mobile-clients-drawer-backdrop--open' : '')
        }
        onClick={() => setIsSortDrawerOpen(false)}
      />
      <div
        className={
          'mobile-clients-drawer' +
          (isSortDrawerOpen ? ' mobile-clients-drawer--open' : '')
        }
      >
        <div className="mobile-clients-drawer-header">
          <h3 className="mobile-clients-drawer-title">Sort Clients</h3>
          <button
            type="button"
            className="mobile-clients-drawer-close"
            onClick={() => setIsSortDrawerOpen(false)}
            aria-label="Close sort"
          >
            <MoreVertIcon />
          </button>
        </div>
        <button
          type="button"
          className="mobile-clients-outline-button"
          onClick={() => handleSortChange('name')}
        >
          Sort by Name {sortBy === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
        </button>
        <button
          type="button"
          className="mobile-clients-outline-button"
          onClick={() => handleSortChange(listMode === 'archived' ? 'archivedAt' : 'createdAt')}
        >
          Sort by {listMode === 'archived' ? 'Date Archived' : 'Date Added'}{' '}
          {sortBy === (listMode === 'archived' ? 'archivedAt' : 'createdAt')
            ? (sortDirection === 'asc' ? '↑' : '↓')
            : ''}
        </button>
      </div>

      <nav className="mobile-clients-bottom-nav">
        <button
          type="button"
          onClick={fetchClientsWithAnimation}
        >
          <RefreshIcon
            style={{
              animation: isLoading ? 'spin 1s linear infinite' : 'none',
            }}
          />
          <span>Refresh</span>
        </button>
        <button
          type="button"
          onClick={listMode === 'active' ? handleAddClient : undefined}
          disabled={listMode === 'archived'}
          style={listMode === 'archived' ? { opacity: 0.45 } : undefined}
        >
          <AddIcon />
          <span>Add</span>
        </button>
        <button
          type="button"
          onClick={handleMoreClick}
        >
          <MoreVertIcon />
          <span>More</span>
        </button>
      </nav>

      {isMoreMenuOpen && (
        <>
          <div
            className="mobile-clients-drawer-backdrop mobile-clients-drawer-backdrop--open"
            onClick={handleMoreClose}
          />
          <div className="mobile-clients-more-menu">
            <div
              className="mobile-clients-more-menu-item"
              onClick={() => {
                handleInviteClientClick();
                handleMoreClose();
              }}
            >
              <AddIcon fontSize="small" />
              <span>Invite Client</span>
            </div>
            <div
              className="mobile-clients-more-menu-item"
              onClick={() => {
                handleImportClick();
                handleMoreClose();
              }}
            >
              <ImportExportIcon fontSize="small" />
              <span>Import Clients</span>
            </div>
            <div
              className="mobile-clients-more-menu-item"
              onClick={() => {
                handleExportClients();
                handleMoreClose();
              }}
            >
              <ImportExportIcon
                fontSize="small"
                style={{ transform: 'rotate(180deg)' }}
              />
              <span>Export Clients</span>
            </div>
            <div
              className="mobile-clients-more-menu-item"
              onClick={() => {
                handleOpenSortDrawer();
                handleMoreClose();
              }}
            >
              <SortIcon fontSize="small" />
              <span>Sort Clients</span>
            </div>
          </div>
        </>
      )}

      <ClientDialog 
        open={isClientDialogOpen}
        onClose={() => setIsClientDialogOpen(false)}
        client={selectedClient}
        existingClients={clients}
        onSave={handleSaveClient}
      />

      <ImportClientsDialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={fetchClients}
      />

      <InviteClientDialog
        open={isInviteClientOpen}
        onClose={() => setIsInviteClientOpen(false)}
        companyData={companyData}
        onInvited={() => setNotification({ open: true, message: 'Client invite sent', severity: 'success' })}
        description="Send a portal invite so clients can create an account."
      />
    </div>
  );
};

export default MobileClientManagement;
